import { Plus, Trash2, X } from "lucide-react";
import * as React from "react";

import type { FieldType } from "@/api/forms";
import { type CustomFieldOut, overridesApi } from "@/api/overrides";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FIELD_TYPE_LABELS } from "@/stores/formBuilder";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  formCode: string;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

const SIMPLE_TYPES: FieldType[] = [
  "text",
  "textarea",
  "number",
  "date",
  "datetime",
  "select",
  "async_select",
  "checkbox",
  "radio",
];

/**
 * CustomFieldDialog — admin thêm field tenant-specific ngoài platform default.
 *
 * Doc 23 §13.3: vd CFO yêu cầu mọi PR phải có "Mã dự án" → admin tạo
 * `project_id` async_select bind vào `project.project`.
 */
export function CustomFieldDialog({
  open,
  formCode,
  onClose,
  onChanged,
}: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<CustomFieldOut[]>([]);
  const [name, setName] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [type, setType] = React.useState<FieldType>("text");
  const [required, setRequired] = React.useState(false);
  const [uiOnly, setUiOnly] = React.useState(true);
  const [erpModel, setErpModel] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await overridesApi.listCustomFields(formCode);
      setItems(list);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi load custom fields",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [formCode, pushToast]);

  React.useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  if (!open) return null;

  const handleCreate = async (): Promise<void> => {
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(name)) {
      pushToast({
        variant: "warning",
        title: "field_name phải snake_case",
      });
      return;
    }
    if (!label) {
      pushToast({ variant: "warning", title: "Cần label" });
      return;
    }
    const schema: Record<string, unknown> = {
      name,
      type,
      label,
      required,
    };
    if (type === "async_select" && erpModel) {
      schema.binding = { erp_model: erpModel };
    }
    setBusy(true);
    try {
      await overridesApi.createCustomField(formCode, {
        field_name: name,
        schema,
        ui_only: uiOnly,
      });
      pushToast({ variant: "success", title: `Đã thêm ${name}` });
      setName("");
      setLabel("");
      await refresh();
      await onChanged();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Tạo thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (fieldName: string): Promise<void> => {
    if (!confirm(`Xoá custom field ${fieldName}?`)) return;
    setBusy(true);
    try {
      await overridesApi.deleteCustomField(formCode, fieldName);
      pushToast({ variant: "success", title: `Đã xoá ${fieldName}` });
      await refresh();
      await onChanged();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-background">
          <div>
            <h2 className="font-semibold">Custom field</h2>
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
              {formCode}
            </code>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-6">
          {/* Existing */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Custom field hiện có</h3>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                Chưa có custom field cho form này.
              </p>
            ) : (
              <div className="space-y-1">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-center gap-2 rounded border bg-muted/30 p-2"
                  >
                    <code className="text-xs bg-background px-1 rounded">
                      {it.field_name}
                    </code>
                    <span className="text-xs">
                      {String(it.schema.label ?? "")}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {String(it.schema.type ?? "?")}
                    </Badge>
                    {it.ui_only ? (
                      <Badge variant="secondary" className="text-[10px]">
                        UI-only
                      </Badge>
                    ) : null}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-7 ml-auto"
                      onClick={() => void handleDelete(it.field_name)}
                      disabled={busy}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* New */}
          <section className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Thêm custom field mới</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1">
                  Tên field (snake_case)
                </label>
                <Input
                  value={name}
                  onChange={(e) =>
                    setName(
                      e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                    )
                  }
                  placeholder="project_id"
                  className="h-8 text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Label</label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Mã dự án"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as FieldType)}
                  className="w-full h-8 rounded-md border bg-background px-2 text-sm"
                >
                  {SIMPLE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {FIELD_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              {type === "async_select" ? (
                <div>
                  <label className="text-xs font-medium block mb-1">
                    ERP model
                  </label>
                  <Input
                    value={erpModel}
                    onChange={(e) => setErpModel(e.target.value)}
                    placeholder="project.project"
                    className="h-8 text-sm font-mono"
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={required}
                    onChange={(e) => setRequired(e.target.checked)}
                  />
                  Bắt buộc
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={uiOnly}
                    onChange={(e) => setUiOnly(e.target.checked)}
                  />
                  UI only (không persist vào ERP — Phase 3)
                </label>
              </div>
            </div>
          </section>
        </div>

        <footer className="px-4 py-3 border-t flex justify-end gap-2 sticky bottom-0 bg-background">
          <Button variant="outline" onClick={onClose}>
            Đóng
          </Button>
          <Button onClick={() => void handleCreate()} disabled={busy}>
            <Plus className="size-3.5 mr-1" /> Thêm field
          </Button>
        </footer>
      </div>
    </div>
  );
}
